import { useEffect, useState } from "react";
import { Payment } from "@mercadopago/sdk-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Copy, Check, RefreshCw, Loader2, ExternalLink } from "lucide-react";

export default function MercadoPagoPayment({ booking, onPaid }) {
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);
  const [checking, setChecking] = useState(false);
  const [copied, setCopied] = useState(false);

  async function handleSubmit({ formData }) {
    setError(null);
    try {
      const r = await api.post("/payments/mercadopago/process", {
        booking_id: booking.id,
        form_data: formData,
      });
      setResult(r.data);
      if (r.data.status === "approved") {
        toast.success("Pagamento aprovado!");
        onPaid?.();
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Falha ao processar o pagamento");
      throw e;
    }
  }

  async function fetchStatus(silent = false) {
    if (!silent) setChecking(true);
    try {
      const r = await api.get(`/payments/mercadopago/${booking.id}/status`);
      if (r.data.payment_status === "pago") {
        toast.success("Pagamento confirmado!");
        onPaid?.();
      } else if (r.data.payment_status === "expirado") {
        setResult((prev) => (prev ? { ...prev, status: "expired" } : prev));
        if (!silent) toast.error("O QR Code expirou. Fale com a barbearia.");
      } else if (!silent) {
        toast.info("Pagamento ainda não confirmado. Tente novamente em instantes.");
      }
    } catch {
      if (!silent) toast.error("Não foi possível verificar o pagamento");
    } finally {
      if (!silent) setChecking(false);
    }
  }

  // verifica automaticamente enquanto o QR/aguardo estiver na tela
  useEffect(() => {
    if (!result || result.status === "approved" || result.status === "expired") {
      return undefined;
    }
    const timer = setInterval(() => fetchStatus(true), 8000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result]);

  function copyPix() {
    navigator.clipboard.writeText(result.qr_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div data-testid="mercadopago-payment" className="mt-4">
      {!result && (
        <div className="rounded-xl border border-[#009ee3]/30 bg-white p-3">
          <p className="mb-2 px-1 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
            Pagamento online · Mercado Pago
          </p>
          {!ready && (
            <div
              data-testid="mp-brick-loading"
              className="flex items-center justify-center gap-2 py-8 text-sm text-zinc-500"
            >
              <Loader2 className="h-4 w-4 animate-spin" /> Carregando pagamento...
            </div>
          )}
          <Payment
            initialization={{
              amount: Number(booking.price),
              payer: {
                email: `cliente${booking.phone}@resenhaducorte.com`,
                firstName: booking.name.split(" ")[0],
                lastName: booking.name.split(" ").slice(1).join(" ") || "Cliente",
              },
            }}
            customization={{
              paymentMethods: {
                creditCard: "all",
                debitCard: "all",
                prepaidCard: "all",
                bankTransfer: "all",
              },
            }}
            onSubmit={handleSubmit}
            onReady={() => setReady(true)}
            onError={(e) => {
              console.error("MP Brick error", e);
              setError("Não foi possível carregar o pagamento.");
            }}
          />
        </div>
      )}

      {error && (
        <p
          data-testid="mp-payment-error"
          className="mt-3 rounded-xl border border-red-500/40 bg-red-500/10 p-3 text-center text-xs text-red-300"
        >
          {error}
        </p>
      )}

      {result?.qr_code_base64 && (
        <div className="flex flex-col items-center rounded-xl border border-[#009ee3]/30 bg-[#0d0d0e] p-4">
          <p className="eyebrow !text-[10px]">Escaneie o QR Code Pix</p>
          <img
            data-testid="mp-pix-qr-code"
            src={`data:image/png;base64,${result.qr_code_base64}`}
            alt="QR Code Pix Mercado Pago"
            className="mt-3 h-48 w-48 rounded-lg bg-white p-2"
          />
          <button
            data-testid="mp-pix-copy-button"
            onClick={copyPix}
            className="mt-3 flex items-center gap-2 rounded-full border border-[#009ee3]/50 px-4 py-2 text-xs font-bold uppercase tracking-wider text-[#009ee3] transition-colors hover:bg-[#009ee3]/10"
          >
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copied ? "Copiado!" : "Copiar Pix copia e cola"}
          </button>
          {result.ticket && (
            <a
              data-testid="mp-ticket-link"
              href={result.ticket}
              target="_blank"
              rel="noreferrer"
              className="mt-2 flex items-center gap-1 text-xs text-zinc-500 underline hover:text-zinc-300"
            >
              Abrir instruções de pagamento <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      )}

      {result?.status === "pending" && (
        <p
          data-testid="mp-pending-notice"
          className="mt-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-center text-xs text-amber-200"
        >
          Aguardando confirmação do Pix. A vaga já está reservada para você.
        </p>
      )}

      {result && result.status !== "approved" && (
        <button
          data-testid="mp-check-payment-button"
          onClick={() => fetchStatus()}
          disabled={checking}
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-zinc-700 py-3 text-xs font-bold uppercase tracking-wider text-zinc-300 transition-colors hover:border-amber-500/50 hover:text-amber-300"
        >
          <RefreshCw className={`h-4 w-4 ${checking ? "animate-spin" : ""}`} />
          Já paguei, verificar
        </button>
      )}
    </div>
  );
}
